/**
 * Polls — the one everyday WhatsApp message type the sanctioned API has no equivalent for at
 * any price, and the one that matches how coordination actually happens in the field: a shift
 * roster, a delivery slot, a headcount before a van leaves, an incident triaged by the crew
 * who can see it.
 *
 * Nothing here talks to WhatsApp. This module owns what a poll has to look like before it is
 * worth sending, and what a pile of votes means once they come back — including the part
 * operations acts on, which is rarely the winning option and almost always the people who
 * never answered.
 */

/** WhatsApp's own client stops at twelve, and a poll it will not render is not worth sending. */
export const POLL_MAX_OPTIONS = 12

/** Two, because a one-option poll is a message with extra steps. */
export const POLL_MIN_OPTIONS = 2

export const POLL_MAX_QUESTION = 255
export const POLL_MAX_OPTION = 100

export interface PollDraft {
  question: string
  options: string[]
  /**
   * How many options one person may pick. `1` is the ordinary case — pick a shift, pick a
   * slot. `0` is WhatsApp's own encoding of "any number of them", which is what a skills or
   * availability poll wants.
   */
  selectableCount: number
}

/**
 * Cleans a draft up and says no when it cannot be counted.
 *
 * The duplicate-option refusal is the one rule here that is not cosmetic. A vote does not
 * come back naming the option: it comes back as the SHA-256 of the option's text, so two
 * options both reading "Yes" are the *same* value on the wire and a tally would have to guess
 * which one a person meant. WhatsApp's own client accepts the duplicate and then miscounts
 * it; refusing it before it is sent is the only point at which that is fixable.
 */
export function normalisePoll(input: {
  question?: unknown
  options?: unknown
  selectableCount?: unknown
}): { poll: PollDraft | null; error: string | null } {
  const question = typeof input.question === 'string' ? input.question.trim() : ''
  if (!question) return { poll: null, error: 'A poll needs a question.' }
  if (question.length > POLL_MAX_QUESTION) {
    return { poll: null, error: `A poll question can be at most ${POLL_MAX_QUESTION} characters.` }
  }

  const raw = Array.isArray(input.options) ? input.options : []
  const options = raw
    .map((option) => (typeof option === 'string' ? option.trim() : ''))
    .filter((option) => option.length > 0)

  if (options.length < POLL_MIN_OPTIONS) {
    return { poll: null, error: `A poll needs at least ${POLL_MIN_OPTIONS} options.` }
  }
  if (options.length > POLL_MAX_OPTIONS) {
    return { poll: null, error: `A poll can have at most ${POLL_MAX_OPTIONS} options.` }
  }
  if (options.some((option) => option.length > POLL_MAX_OPTION)) {
    return { poll: null, error: `A poll option can be at most ${POLL_MAX_OPTION} characters.` }
  }

  // Case-insensitively, because "Yes" and "yes" hash differently and so would count
  // separately, which is a distinction no operator writing that poll intended to draw.
  const seen = new Set<string>()
  for (const option of options) {
    const key = option.toLocaleLowerCase()
    if (seen.has(key)) {
      return { poll: null, error: `Two options both read "${option}". Votes could not be told apart.` }
    }
    seen.add(key)
  }

  const requested = typeof input.selectableCount === 'number' ? input.selectableCount : 1
  if (!Number.isInteger(requested) || requested < 0 || requested > options.length) {
    return { poll: null, error: 'A poll must let people pick between 0 (any) and every option.' }
  }

  return { poll: { question, options, selectableCount: requested }, error: null }
}

/**
 * What a poll reads as in a thread.
 *
 * A poll carries no body WhatsApp would recognise as one, so the inbox, the CSV export, the
 * conversation preview and every keyword rule would all see an empty message. This literal is
 * asserted against `pollBody` in the session host's `normalize.ts` as well: a poll we sent and
 * a poll a group member sent reach the same thread down two entirely separate code paths, and
 * matching literals is the only thing keeping them reading alike.
 */
export function pollBody(poll: { question: string; options: string[] }): string {
  const question = poll.question.trim() || 'Poll'
  const options = poll.options.map((option) => option.trim()).filter(Boolean)
  return options.length === 0 ? `Poll: ${question}` : `Poll: ${question} — ${options.join(' / ')}`
}

export interface PollVoteRecord {
  voterPhone: string
  voterName?: string | null
  /** The full selection, not a delta — see `tallyPoll`. */
  options: string[]
  votedAt: Date
}

export interface PollOptionTally {
  option: string
  votes: number
  /** 0–1 of the people who voted at all, not of the group. */
  share: number
  voters: string[]
}

export interface PollTally {
  options: PollOptionTally[]
  /** People, not selections: a multi-select poll has more selections than voters. */
  voters: number
  leading: string | null
  /** True when two or more options are tied at the top and there is no answer to act on. */
  tied: boolean
}

/**
 * Each voter's standing selection.
 *
 * One row per voter, and the last row wins. That is not a policy choice — WhatsApp re-sends a
 * voter's *entire* selection every time they change it rather than a diff, so a person who
 * picks Tuesday, changes to Wednesday, then adds Thursday produces three payloads of which
 * only the third describes what they want. Accumulating them would report a crew of six as
 * fourteen people.
 */
function latestPerVoter(votes: PollVoteRecord[]): Map<string, PollVoteRecord> {
  const latest = new Map<string, PollVoteRecord>()
  for (const vote of votes) {
    const held = latest.get(vote.voterPhone)
    if (!held || held.votedAt <= vote.votedAt) latest.set(vote.voterPhone, vote)
  }
  return latest
}

/**
 * The people who actually answered: those whose standing selection is not empty.
 *
 * An empty latest selection is not an answer. It is either a deselection — WhatsApp sends the
 * whole selection on every change, so clearing your pick arrives as `[]` — or a vote whose
 * digests `recordPollVote` could not place against the option list, which it stores as `[]`
 * rather than inventing an answer. Counting either as a voter is the one direction this module
 * was written never to fail in: a poll that reads as answered by everyone with an empty chase
 * list, headlined by a leader that does not exist. See HANDOVER §8.1.
 */
function answeredPhones(votes: PollVoteRecord[]): Set<string> {
  const answered = new Set<string>()
  for (const [phone, vote] of latestPerVoter(votes)) {
    if (vote.options.length > 0) answered.add(phone)
  }
  return answered
}

/**
 * Counts a poll.
 *
 * A voter with an empty standing selection is not counted — see `answeredPhones`.
 */
export function tallyPoll(options: string[], votes: PollVoteRecord[]): PollTally {
  const counted = [...latestPerVoter(votes).values()].filter((vote) => vote.options.length > 0)
  const tallies: PollOptionTally[] = options.map((option) => {
    const voters = counted.filter((vote) => vote.options.includes(option)).map((v) => v.voterPhone)
    return {
      option,
      votes: voters.length,
      share: counted.length === 0 ? 0 : voters.length / counted.length,
      voters,
    }
  })

  const top = Math.max(0, ...tallies.map((t) => t.votes))
  const leaders = tallies.filter((t) => t.votes === top && top > 0)

  return {
    options: tallies,
    voters: counted.length,
    leading: leaders.length === 1 ? leaders[0].option : null,
    tied: leaders.length > 1,
  }
}

export interface PollParticipation {
  /** Everyone the poll was put in front of, when the group's membership is known. */
  asked: number
  answered: number
  /** Members who have not voted. The list operations actually chases. */
  silent: string[]
  /** 0–1. `null` when the group's membership is unknown, which is not the same as zero. */
  turnout: number | null
}

/**
 * Who answered and who did not.
 *
 * `turnout` is null rather than 0 when membership is unknown, because a screen that cannot
 * tell "nobody replied" from "we do not know who was asked" will report the second as the
 * first, and those two call for opposite actions.
 */
export function pollParticipation(
  votes: PollVoteRecord[],
  members: string[] | null,
): PollParticipation {
  const answered = answeredPhones(votes)
  if (!members) {
    return { asked: 0, answered: answered.size, silent: [], turnout: null }
  }

  const unique = [...new Set(members)]
  const silent = unique.filter((phone) => !answered.has(phone))
  return {
    asked: unique.length,
    // Someone can vote and then leave the group, so this counts against the roster rather
    // than against the vote rows — otherwise turnout could exceed 100%.
    answered: unique.length - silent.length,
    silent,
    turnout: unique.length === 0 ? null : (unique.length - silent.length) / unique.length,
  }
}

/**
 * The line a dispatcher reads without opening anything.
 *
 * A tie is stated as a tie. The single most costly thing this feature could do is report a
 * winning shift that six people did not in fact agree on.
 */
export function pollSentence(tally: PollTally, participation: PollParticipation): string {
  if (tally.voters === 0) {
    if (participation.turnout === null) return 'No votes yet.'
    return `No votes yet, from ${participation.asked} ${participation.asked === 1 ? 'member' : 'members'}.`
  }

  // `leading` can be null with no tie when votes came in but none land on an option this side
  // offered — the two ends disagree about the option list. Naming the null leader here is what
  // rendered the literal "null leads with 0"; there is no winner to state, so we say so.
  const head = tally.tied
    ? 'No clear answer: the top options are tied'
    : tally.leading === null
      ? 'No clear answer yet'
      : `${tally.leading} leads with ${tally.options.find((o) => o.option === tally.leading)?.votes ?? 0}`

  if (participation.turnout === null) {
    return `${head}, on ${tally.voters} ${tally.voters === 1 ? 'vote' : 'votes'}.`
  }
  return `${head}, on ${participation.answered} of ${participation.asked} members.`
}
